// canvas-transition.ts — THE ANSWER DIFF ENGINE (the Living Canvas).
//
// Given the canvas that is currently on screen (previousCanvas) and the canvas the next
// governed answer wants to show (nextCanvas), emit a CanvasTransition that says — by
// IDENTITY, never by position or label — which components persist unchanged, which persist
// but carry changed governed numbers (binding deltas), which enter (and at which layout
// slot), and which exit.
//
// Identity law (the whole point of this module):
// - A COMPONENT is the same component iff `componentId` AND `componentType` are equal.
//   Position in the answer, section label, and prop contents play NO part in matching.
// - A NUMBER is the same fact iff its `bindingId` (the governed numeric-binding
//   placeholder, e.g. gml_* / flagship_* / ranked_driver_*) is equal. The same binding id
//   across consecutive answers IS the same fact — it morphs in place; it is never rebuilt.
// - Anything not matched by identity enters or exits. A component that keeps its id but
//   changes type is NOT the same component (the closed registry treats type as identity):
//   it exits and a new one enters.
//
// Honesty law: a delta endpoint whose governed value is withheld carries `value: null`
// and its honest formatted string (or ''). Consumers must NEVER tween through a null
// endpoint — a withheld number appears/disappears instantly; only governed-number →
// governed-number changes animate. This module only reports; it never fabricates.
//
// Purity: no DOM, no React, no Date, no randomness. Same inputs → deep-equal output.

/** Stable identity of one canvas component plus the governed numbers it displays. */
export type CanvasComponentIdentity = {
  /** The closed-registry component id (e.g. `value_creation_decision:RankedDriverTable:sga-lever-value-ranking`). */
  componentId: string;
  /** The closed-registry component type. Part of identity. */
  componentType: string;
  /**
   * Binding ids (numeric placeholder identities) this component displays, in the
   * component's own display order. Optional: a component with no governed numbers
   * simply has none.
   */
  bindingIds?: readonly string[];
};

/**
 * One comparative frame of a governed number (THE COMPARATIVE PIVOT). When an answer
 * re-poses the same fact across scenario frames ("improve EBITDA by 15%" → "what about
 * 10%?"), the server emits the binding with variant-paired values instead of a single
 * value. `frameLabel` is the frame's identity (frames match across answers by label,
 * never by column position).
 */
export type CanvasBindingVariant = {
  /** Frame identity, e.g. '15% target' / '10% target'. */
  frameLabel: string;
  value: number | null;
  formattedValue: string;
  /**
   * Governed delta vs the anchor frame (e.g. '−2,000 roles'). NEVER populated by the
   * server: per the #2032 contract, governed deltas ship as SEPARATE numeric bindings
   * named `objective_variant_delta__<bindingId>`. A client adapter MAY copy the matching
   * delta binding's formatted string into this slot for convenience; the engine relays
   * whatever is present verbatim and never computes a delta client-side (never-sum law).
   */
  deltaFormattedValue?: string | null;
};

/** One governed number: identity + raw value + the exact formatted string rendered. */
export type CanvasBindingValue = {
  /** The stable binding id (`WorkspaceNumericBinding.placeholder`). */
  bindingId: string;
  /** Governed raw value; null when the value is withheld/pending. */
  value: number | null;
  /** The governed formatted string as rendered (e.g. "$48.0M"); '' when withheld. */
  formattedValue: string;
  /**
   * Comparative frames, when the answer poses this fact across scenario variants.
   * Present with length >= 2 on a comparative answer; absent/undefined on a plain one.
   * SERVER CONTRACT (#2032, verified): when present, `value`/`formattedValue` above
   * mirror the NEW frame (the newly asked case), NOT the anchor. Renderers must render
   * columns from `variants`, never from the top-level value. The diff engine never reads
   * the next binding's top-level value while matching: the anchor resolves from the
   * PREVIOUS answer's endpoint, with variants[0] (the prior frame) as fallback.
   */
  variants?: readonly CanvasBindingVariant[] | null;
};

export type CanvasSnapshot = {
  /** Components in the answer's layout order. Order is layout, NOT identity. */
  components: readonly CanvasComponentIdentity[];
  bindings: readonly CanvasBindingValue[];
};

export type CanvasBindingDeltaEndpoint = {
  value: number | null;
  formattedValue: string;
};

export type CanvasBindingDelta = {
  bindingId: string;
  /** Present when the delta is scoped to one comparative frame of a pivoted binding. */
  frameLabel?: string;
  from: CanvasBindingDeltaEndpoint;
  to: CanvasBindingDeltaEndpoint;
};

/**
 * THE COMPARATIVE PIVOT for one binding: the value already on screen becomes the anchor
 * column (it slides aside), and the newly introduced frames appear beside it.
 */
export type CanvasBindingPivot = {
  bindingId: string;
  /** The frame the previously displayed value belongs to — it slides to the left column. */
  anchorFrame: CanvasBindingVariant;
  /** Newly introduced comparative frames, in payload order — they fade in beside the anchor. */
  addedFrames: CanvasBindingVariant[];
};

export type CanvasEnteringComponent = {
  component: CanvasComponentIdentity;
  /** Index in nextCanvas.components — the layout slot the card squeezes into. */
  insertIndex: number;
};

export type CanvasUpdatedComponent = {
  componentId: string;
  componentType: string;
  bindingDeltas: CanvasBindingDelta[];
};

/**
 * A matched component whose bindings grew comparative frames: same identity, the card
 * does not rebuild — it grows columns. Any same-frame value changes on its other
 * bindings ride along as `bindingDeltas` so the component is classified exactly once.
 */
export type CanvasPivotedComponent = {
  componentId: string;
  componentType: string;
  pivots: CanvasBindingPivot[];
  bindingDeltas: CanvasBindingDelta[];
};

export type CanvasTransition = {
  /** Matched components with NO binding delta, in next-canvas layout order. They may
   *  still move to a new layout slot — the motion layer FLIPs them there. */
  persisted: string[];
  /** Matched components whose governed numbers changed, in next-canvas layout order. */
  updated: CanvasUpdatedComponent[];
  /** Matched components whose bindings grew comparative frames, in next-canvas order. */
  pivoted: CanvasPivotedComponent[];
  /** Components present only in the next canvas, ascending by insertIndex. */
  entering: CanvasEnteringComponent[];
  /** Components present only in the previous canvas, in previous-canvas order. */
  exiting: string[];
};

const IDENTITY_SEPARATOR = '\u0000';

function componentIdentityKey(component: CanvasComponentIdentity): string {
  return `${component.componentId}${IDENTITY_SEPARATOR}${component.componentType}`;
}

/**
 * First occurrence wins on a duplicated identity (admission rejects duplicate component
 * ids upstream; this keeps the engine deterministic even on malformed input).
 */
function componentsByIdentity(
  components: readonly CanvasComponentIdentity[],
): Map<string, { component: CanvasComponentIdentity; index: number }> {
  const map = new Map<string, { component: CanvasComponentIdentity; index: number }>();
  components.forEach((component, index) => {
    const key = componentIdentityKey(component);
    if (!map.has(key)) {
      map.set(key, { component, index });
    }
  });
  return map;
}

function bindingsById(
  bindings: readonly CanvasBindingValue[],
): Map<string, CanvasBindingValue> {
  const map = new Map<string, CanvasBindingValue>();
  for (const binding of bindings) {
    if (!map.has(binding.bindingId)) {
      map.set(binding.bindingId, binding);
    }
  }
  return map;
}

const WITHHELD_ENDPOINT: CanvasBindingDeltaEndpoint = { value: null, formattedValue: '' };

function deltaEndpoint(binding: CanvasBindingValue | undefined): CanvasBindingDeltaEndpoint {
  if (!binding) {
    return WITHHELD_ENDPOINT;
  }
  return { value: binding.value, formattedValue: binding.formattedValue };
}

function endpointsDiffer(
  from: CanvasBindingDeltaEndpoint,
  to: CanvasBindingDeltaEndpoint,
): boolean {
  // Object.is keeps NaN/-0 handling deterministic; the formatted string is part of the
  // rendered fact, so a formatting change alone is also a delta.
  return !Object.is(from.value, to.value) || from.formattedValue !== to.formattedValue;
}

function variantEndpoint(variant: CanvasBindingVariant): CanvasBindingDeltaEndpoint {
  return { value: variant.value, formattedValue: variant.formattedValue };
}

type ComponentBindingChanges = {
  pivots: CanvasBindingPivot[];
  deltas: CanvasBindingDelta[];
};

/**
 * Pivot detection for one binding. A pivot is the frame set GROWING: a single value
 * becoming a comparative pair (or an existing pair gaining frames). Frames match across
 * answers by `frameLabel` — frame identity, never column position.
 *
 * Anchor selection (single → multi): the variant whose value AND formatted string equal
 * the value already on screen (the number the viewer is watching slide aside). When no
 * variant matches — the server re-based the anchor — the FIRST variant is the anchor and
 * the value change surfaces as an honest frame-scoped delta alongside the pivot.
 */
function bindingChange(
  bindingId: string,
  previous: CanvasBindingValue | undefined,
  next: CanvasBindingValue | undefined,
): { pivot: CanvasBindingPivot | null; deltas: CanvasBindingDelta[] } {
  const nextVariants = next?.variants ?? [];
  const previousVariants = previous?.variants ?? [];

  if (nextVariants.length >= 2) {
    const deltas: CanvasBindingDelta[] = [];

    if (previousVariants.length >= 2) {
      const previousByLabel = new Map(
        previousVariants.map((variant) => [variant.frameLabel, variant]),
      );
      const nextLabels = new Set(nextVariants.map((variant) => variant.frameLabel));
      const addedFrames = nextVariants.filter(
        (variant) => !previousByLabel.has(variant.frameLabel),
      );
      // Same-frame value changes ride along as frame-scoped deltas (frame identity).
      for (const variant of nextVariants) {
        const previousVariant = previousByLabel.get(variant.frameLabel);
        if (!previousVariant) continue;
        const from = variantEndpoint(previousVariant);
        const to = variantEndpoint(variant);
        if (endpointsDiffer(from, to)) {
          deltas.push({ bindingId, frameLabel: variant.frameLabel, from, to });
        }
      }
      // A frame that disappeared is honestly reported as gone (never tweened).
      for (const previousVariant of previousVariants) {
        if (!nextLabels.has(previousVariant.frameLabel)) {
          deltas.push({
            bindingId,
            frameLabel: previousVariant.frameLabel,
            from: variantEndpoint(previousVariant),
            to: WITHHELD_ENDPOINT,
          });
        }
      }
      if (addedFrames.length === 0) {
        return { pivot: null, deltas };
      }
      const anchorFrame =
        nextVariants.find((variant) => previousByLabel.has(variant.frameLabel)) ??
        nextVariants[0]!;
      return { pivot: { bindingId, anchorFrame, addedFrames }, deltas };
    }

    // Single value → comparative frames: the founder's 15% → "what about 10%?" turn.
    const previousEndpoint = deltaEndpoint(previous);
    const matchedAnchor = nextVariants.find(
      (variant) =>
        Object.is(variant.value, previousEndpoint.value) &&
        variant.formattedValue === previousEndpoint.formattedValue,
    );
    const anchorFrame = matchedAnchor ?? nextVariants[0]!;
    if (!matchedAnchor && previous) {
      const to = variantEndpoint(anchorFrame);
      if (endpointsDiffer(previousEndpoint, to)) {
        deltas.push({
          bindingId,
          frameLabel: anchorFrame.frameLabel,
          from: previousEndpoint,
          to,
        });
      }
    }
    const addedFrames = nextVariants.filter((variant) => variant !== anchorFrame);
    return { pivot: { bindingId, anchorFrame, addedFrames }, deltas };
  }

  return { pivot: null, deltas: [] };
}

/**
 * Binding changes for one matched component: keyed on binding identity, ordered by the
 * NEXT component's bindingIds order (deterministic). A binding id the component carried
 * before but no longer carries yields a delta to the withheld endpoint — the honest
 * "this number is gone" signal (never tweened).
 */
function componentBindingChanges(
  previousComponent: CanvasComponentIdentity,
  nextComponent: CanvasComponentIdentity,
  previousBindings: Map<string, CanvasBindingValue>,
  nextBindings: Map<string, CanvasBindingValue>,
): ComponentBindingChanges {
  const nextIds = nextComponent.bindingIds ?? [];
  const previousIds = previousComponent.bindingIds ?? [];
  const seen = new Set<string>();
  const orderedIds: string[] = [];
  for (const bindingId of nextIds) {
    if (!seen.has(bindingId)) {
      seen.add(bindingId);
      orderedIds.push(bindingId);
    }
  }
  for (const bindingId of previousIds) {
    if (!seen.has(bindingId)) {
      seen.add(bindingId);
      orderedIds.push(bindingId);
    }
  }

  const pivots: CanvasBindingPivot[] = [];
  const deltas: CanvasBindingDelta[] = [];
  for (const bindingId of orderedIds) {
    const carriedBefore = previousIds.includes(bindingId);
    const carriedNext = nextIds.includes(bindingId);
    const previous = carriedBefore ? previousBindings.get(bindingId) : undefined;
    const next = carriedNext ? nextBindings.get(bindingId) : undefined;

    const change = bindingChange(bindingId, previous, next);
    if (change.pivot) {
      pivots.push(change.pivot);
      deltas.push(...change.deltas);
      continue;
    }
    deltas.push(...change.deltas);
    if ((next?.variants?.length ?? 0) >= 2) {
      continue; // variant-carrying binding without growth: frame deltas already handled
    }

    const from = deltaEndpoint(previous);
    const to = deltaEndpoint(next);
    if (endpointsDiffer(from, to)) {
      deltas.push({ bindingId, from, to });
    }
  }
  return { pivots, deltas };
}

/**
 * Diff two canvas snapshots into a CanvasTransition.
 *
 * Deterministic and pure. Matching is identity-only:
 * - persisted/updated: identity present in both snapshots (updated when any governed
 *   number the component displays changed value or formatted string);
 * - entering: identity present only in next (insertIndex = its slot in next order);
 * - exiting: identity present only in previous (in previous order).
 */
export function diffCanvasSnapshots(
  previousCanvas: CanvasSnapshot,
  nextCanvas: CanvasSnapshot,
): CanvasTransition {
  const previousByIdentity = componentsByIdentity(previousCanvas.components);
  const nextByIdentity = componentsByIdentity(nextCanvas.components);
  const previousBindingMap = bindingsById(previousCanvas.bindings);
  const nextBindingMap = bindingsById(nextCanvas.bindings);

  const persisted: string[] = [];
  const updated: CanvasUpdatedComponent[] = [];
  const pivoted: CanvasPivotedComponent[] = [];
  const entering: CanvasEnteringComponent[] = [];
  const exiting: string[] = [];

  const seenNextIdentityKeys = new Set<string>();
  nextCanvas.components.forEach((nextComponent, index) => {
    const key = componentIdentityKey(nextComponent);
    if (seenNextIdentityKeys.has(key)) {
      return; // duplicate identity in next snapshot: first occurrence already handled
    }
    seenNextIdentityKeys.add(key);

    const previousEntry = previousByIdentity.get(key);
    if (!previousEntry) {
      entering.push({ component: nextComponent, insertIndex: index });
      return;
    }

    const { pivots, deltas } = componentBindingChanges(
      previousEntry.component,
      nextComponent,
      previousBindingMap,
      nextBindingMap,
    );
    // Each matched component lands in exactly one class; a pivot is the richest.
    if (pivots.length > 0) {
      pivoted.push({
        componentId: nextComponent.componentId,
        componentType: nextComponent.componentType,
        pivots,
        bindingDeltas: deltas,
      });
    } else if (deltas.length > 0) {
      updated.push({
        componentId: nextComponent.componentId,
        componentType: nextComponent.componentType,
        bindingDeltas: deltas,
      });
    } else {
      persisted.push(nextComponent.componentId);
    }
  });

  const seenPreviousIdentityKeys = new Set<string>();
  previousCanvas.components.forEach((previousComponent) => {
    const key = componentIdentityKey(previousComponent);
    if (seenPreviousIdentityKeys.has(key)) {
      return;
    }
    seenPreviousIdentityKeys.add(key);
    if (!nextByIdentity.has(key)) {
      exiting.push(previousComponent.componentId);
    }
  });

  return { persisted, updated, pivoted, entering, exiting };
}

/** An empty canvas — the state before the first answer (everything enters). */
export const EMPTY_CANVAS_SNAPSHOT: CanvasSnapshot = Object.freeze({
  components: Object.freeze([]) as readonly CanvasComponentIdentity[],
  bindings: Object.freeze([]) as readonly CanvasBindingValue[],
});
